import { useEffect, useEffectEvent, useRef } from "react";
import { log, track } from "@connect/observability";
import { trackFrameReady } from "../../../../lib/track-frame-ready";
import type { PaymentInstrument, PaymentMethodLabel } from "../../../../lib/buy/create-transaction";
import { decodeSignedQuote } from "../../../../lib/buy/quote";
import type { SignedQuote } from "../../../../lib/buy/quote.types";
import { emitTerminal } from "../../lib/emit-terminal";
import { runHeadlessPurchase } from "../../lib/run-headless-purchase";
import type { PaymentButtonProps } from "../buttons.types";
import { useKycStepUp } from "./use-kyc-step-up";

export function sendHeadlessReady(pm: PaymentButtonProps["pm"], paymentMethod: string): void {
  track.info("connect.buy_button.ready", { paymentMethod });
  trackFrameReady("platform-buy", { paymentMethod });
  pm.send("ready");
}

export interface UseHeadlessPurchaseParams extends PaymentButtonProps {
  paymentMethod: PaymentMethodLabel;
  /** What to pay with, or why this quote can't be paid (sent as `invalidQuote`). */
  instrumentFor: (quote: SignedQuote) => PaymentInstrument | string;
  /** Overridable for tests. */
  run?: typeof runHeadlessPurchase;
}

/**
 * Headless mode: the partner drew its own button, so the purchase runs on mount. A `setQuote` only
 * starts another attempt after `quoteExpired`: the SDK also sends it whenever the partner's quote
 * changes, and that alone must not start a purchase.
 */
export function useHeadlessPurchase({
  signedQuote,
  config,
  pm,
  paymentMethod,
  instrumentFor,
  run = runHeadlessPurchase,
}: UseHeadlessPurchaseParams): void {
  const kycStepUp = useKycStepUp(signedQuote.quoteSignature);
  const cancelledRef = useRef(false);
  const lastQuoteRef = useRef<SignedQuote | null>(null);
  const canRetryRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const attempt = useEffectEvent((quote: SignedQuote) => {
    // StrictMode runs the effect twice in development; the same quote is never bought twice.
    if (quote === lastQuoteRef.current) {
      return;
    }
    const first = lastQuoteRef.current === null;
    if (!first && !canRetryRef.current) {
      log.info("connect.buy_button.set_quote_ignored", { paymentMethod });
      return;
    }
    lastQuoteRef.current = quote;
    canRetryRef.current = false;

    // Like Orion, any quote that decodes gets `ready` first; problems with it come after.
    if (first) {
      sendHeadlessReady(pm, paymentMethod);
    }
    const instrument = instrumentFor(quote);
    if (typeof instrument === "string") {
      void emitTerminal(
        pm,
        { kind: "error", payload: { code: "invalidQuote", message: instrument } },
        paymentMethod,
      );
      return;
    }
    void run({
      quote,
      config,
      pm,
      instrument,
      paymentMethod,
      isCancelled: () => cancelledRef.current,
      kycStepUp,
    }).then(
      (outcome) => {
        canRetryRef.current = outcome === "quoteExpired";
      },
      // The steps report their own failures, so this is a bug. The partner must still hear back.
      (err: unknown) => {
        log.error(err instanceof Error ? err : String(err));
        void emitTerminal(
          pm,
          { kind: "error", payload: { code: "generic", message: "Something went wrong" } },
          paymentMethod,
        );
      },
    );
  });

  // The SDK sends setQuote on every quote change, including empty ones, so a quote that won't
  // decode only gets an answer while a retry is expected. Otherwise the partner could get a
  // second final message for one purchase.
  const reportUnreadableQuote = useEffectEvent(() => {
    if (!canRetryRef.current) {
      log.info("connect.buy_button.set_quote_ignored", { paymentMethod });
      return;
    }
    void emitTerminal(
      pm,
      { kind: "error", payload: { code: "invalidQuote", message: "Could not read the new quote" } },
      paymentMethod,
    );
  });

  // Subscribed once connected, like `attempt` below: before that the client isn't attached yet.
  useEffect(() => {
    let unsubscribe = () => {};
    let disposed = false;
    pm.connected.then(
      () => {
        if (disposed) {
          return;
        }
        unsubscribe = pm.subscribe((message) => {
          if (
            message.kind === "setQuote" &&
            !decodeSignedQuote(message.payload?.quote?.signature ?? "")
          ) {
            reportUnreadableQuote();
          }
        });
      },
      () => {},
    );
    return () => {
      disposed = true;
      unsubscribe();
    };
  }, [pm]);

  // Like Orion, nothing goes out before the handshake: this effect runs before the parent's
  // usePostMessage has a client, so an earlier `ready` would be dropped.
  // A failed handshake is reported by BuyButtonApp.
  useEffect(() => {
    pm.connected.then(
      () => attempt(signedQuote),
      () => {},
    );
  }, [pm, signedQuote]);
}
